import { events } from 'aws-amplify/data';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from 'src/logger';
import { EventsApiContextType } from 'src/components/context/EventsApiContextModels';

const RESPONSE_TIMEOUT_MS = 60000; // 60 seconds timeout for responses

/**
 * Gets the current access token from AWS Cognito session
 * @returns Promise<string | null> - Current access token or null if unavailable
 */
const getCurrentAccessToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession(); // Did not add in context because need to access in real-time to avoid expiration
    const token = session.tokens?.accessToken?.toString() || null;
    if (!token) {
      logger.warn('No access token available for AppSync Events API');
    }
    return token;
  } catch (error) {
    logger.warn('Error fetching current access token:', error as Error);
    return null;
  }
};

export interface SummaryResponse {
  message: string;
  summaryId: string;
}

export const fetchPageSummary = (
  sessionId: string,
  pageType: string,
  filters: string,
  aggregatedData: string,
  eventsApiContext: EventsApiContextType,
  userAlias: string
): Promise<SummaryResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      if (filters === undefined || filters === null || aggregatedData === undefined || aggregatedData === null) {
        throw new Error('filters and aggregatedData are required parameters');
      }

      // Get fresh JWT token (or null if unavailable)
      const jwtToken = await getCurrentAccessToken();

      const currentRequestId = sessionId;

      // Cancel any existing request for this page type
      const wasCanceled = eventsApiContext.cancelPageRequest(pageType);
      if (wasCanceled) {
        logger.info(`Canceled previous request for pageType: ${pageType}`);
      }

      // Store this as the latest request for this pageType (backward compatibility)
      eventsApiContext.setLatestPageRequestId(pageType, currentRequestId);

      // Subscribe to response
      logger.info('Connecting to subscription channel...');
      const pr = events.connect('finsum-subscriber/page');

      let timeout: NodeJS.Timeout | undefined;
      let hasReceived = false;

      // First set up the subscription
      await pr
        .then(async (channel) => {
          logger.info('Connected to subscription channel');

          // Add timeout for no response
          timeout = setTimeout(() => {
            if (eventsApiContext.getLatestPageRequestId(pageType) === currentRequestId && !hasReceived) {
              logger.error('No response received within 60 seconds');
              reject(new Error('No response received within 60 seconds'));
              eventsApiContext.cancelPageRequest(pageType);
            }
          }, RESPONSE_TIMEOUT_MS);

          // Store full request state for resource management
          eventsApiContext.setPageRequestState(pageType, {
            id: currentRequestId,
            timestamp: Date.now(),
            channel,
            timeout
          });

          channel.subscribe({
            next: (data) => {
              logger.info('Received data:', data);

              // Get the latest request ID from context for comparison
              const latestRequestId = eventsApiContext.getLatestPageRequestId(pageType);
              logger.info(`${data.event?.sessionId} === ${latestRequestId}`);
              if (data.event?.sessionId === latestRequestId && latestRequestId !== undefined) {
                hasReceived = true;
                const message = data.event?.message || 'Unable to parse the response for summary';

                // Only process final messages
                if (data.event?.isFinal) {
                  const summaryId = data.event?.summaryId || '';
                  logger.info(`Resolving with final message: ${message}, summaryId: ${summaryId}`);
                  resolve({ message, summaryId });
                  // Clean up using the enhanced method
                  eventsApiContext.cancelPageRequest(pageType);
                }
              } else {
                logger.info('Ignoring response for outdated request:', data.event?.sessionId);
              }
            },
            error: (error) => {
              logger.error('AppSync Events Subscription error:', error);
              reject(error);
              eventsApiContext.cancelPageRequest(pageType);
            }
          });

          // Now that subscription is set up, publish the event
          logger.info('Publishing event:', { sessionId, pageType, filters, aggregatedData, userAlias });
          const event = {
            sessionId: sessionId,
            pageType: pageType,
            filters: filters,
            aggregatedData: aggregatedData,
            userId: userAlias,
            jwtToken: jwtToken
          };

          try {
            await events.post('finsum-publisher/page', event);
            logger.info('Event published successfully');
          } catch (publishError) {
            logger.error('Error publishing event:', publishError as Error);
            eventsApiContext.cancelPageRequest(pageType);
            reject(publishError);
          }
        })
        .catch((error) => {
          logger.error('Connection error:', error);
          eventsApiContext.cancelPageRequest(pageType);
          reject(error);
        });
    } catch (error) {
      logger.error(`Error in fetchPageSummary: ${error}`);
      eventsApiContext.cancelPageRequest(pageType);
      reject(error);
    }
  });
};

export const fetchChartSummary = (
  sessionId: string,
  pageType: string,
  chartType: string,
  chartTitle: string,
  chartData: any[], // Using 'any' for compatibility with AppSync event serialization which expects DocumentType
  eventsApiContext: EventsApiContextType,
  userAlias: string,
  filters?: string
): Promise<SummaryResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Get fresh JWT token (or null if unavailable)
      const jwtToken = await getCurrentAccessToken();

      const currentRequestId = sessionId;

      // Cancel any existing chart request
      const wasCanceled = eventsApiContext.cancelChartRequest();
      if (wasCanceled) {
        logger.info('Canceled previous chart request');
      }

      // Store this as the latest request (backward compatibility)
      eventsApiContext.setLatestChartRequestId(currentRequestId);

      // Subscribe to response
      logger.info('Connecting to subscription channel...');
      const pr = events.connect('finsum-subscriber/chart');

      let timeout: NodeJS.Timeout | undefined;
      let hasReceived = false;

      // First set up the subscription
      await pr
        .then(async (channel) => {
          logger.info('Connected to subscription channel');

          // Add timeout for no response
          timeout = setTimeout(() => {
            if (eventsApiContext.getLatestChartRequestId() === currentRequestId && !hasReceived) {
              logger.error('No response received within 60 seconds');
              reject(new Error('No response received within timeout'));
              eventsApiContext.cancelChartRequest();
            }
          }, RESPONSE_TIMEOUT_MS);

          // Store full request state for resource management
          eventsApiContext.setChartRequestState({
            id: currentRequestId,
            timestamp: Date.now(),
            channel,
            timeout
          });

          channel.subscribe({
            next: (data) => {
              logger.info('Received data:', data);

              // Only process if this response is for the latest request
              const latestChartRequestId = eventsApiContext.getLatestChartRequestId();
              logger.info(`${data.event?.sessionId} === ${latestChartRequestId}`);
              if (data.event?.sessionId === latestChartRequestId) {
                hasReceived = true;
                const message = data.event?.message || 'Unable to parse the response';

                // Only process final messages
                if (data.event?.isFinal) {
                  const summaryId = data.event?.summaryId || '';
                  logger.info(`Resolving with final message: ${message}, summaryId: ${summaryId}`);
                  resolve({ message, summaryId });
                  // Clean up using the enhanced method
                  eventsApiContext.cancelChartRequest();
                }
              } else {
                logger.info('Ignoring response for outdated request:', data.event?.sessionId);
              }
            },
            error: (error) => {
              logger.error('AppSync Events Subscription error:', error);
              reject(error);
              eventsApiContext.cancelChartRequest();
            }
          });

          // Now that subscription is set up, publish the event
          logger.info('Publishing event:', { sessionId, chartType, chartData, chartTitle, filters, userAlias });
          const event = {
            sessionId: sessionId,
            chartType: chartType,
            chartTitle: chartTitle,
            filters: filters || '',
            chartData: chartData || [],
            userId: userAlias,
            pageType: pageType,
            jwtToken: jwtToken
          };

          try {
            await events.post('finsum-publisher/chart', event);
            logger.info('Event published successfully');
          } catch (publishError) {
            logger.error('Error publishing event:', publishError as Error);
            eventsApiContext.cancelChartRequest();
            reject(publishError);
          }
        })
        .catch((error) => {
          logger.error('Connection error:', error);
          eventsApiContext.cancelChartRequest();
          reject(error);
        });
    } catch (error) {
      logger.error(`Error in fetchChartSummary: ${error}`);
      eventsApiContext.cancelChartRequest();
      reject(error);
    }
  });
};
